<template>
  <div class="editor-root">
    <div class="stage-viewport" ref="stageViewport" @contextmenu.prevent>
      <div class="stage" ref="stageWrap">
        <canvas ref="canvas"></canvas>
      </div>
    </div>
     <div class="toolbar">
       <div class="size-tool" v-if="tool==='brush' || tool==='eraser'">
        <span>{{ tool === 'brush' ? '画笔尺寸' : '橡皮擦尺寸' }}</span>
        <Slider v-model="activeSize" :min="2" :max="100" :step="1" style="width:150px;margin:0 8px;"/>
         <span class="size-text">{{ activeSize }}px</span>
       </div>
       <div class="tool-buttons">
         <ButtonGroup>
           <Button :type="tool==='brush'?'primary':'default'" @click="setTool('brush')"><Icon type="ios-brush"/> 画笔</Button>
           <Button :type="tool==='rect'?'primary':'default'" @click="setTool('rect')"><Icon type="ios-crop"/> 框选</Button>
           <Button :type="tool==='eraser'?'primary':'default'" @click="setTool('eraser')"><Icon type="ios-trash"/> 橡皮擦</Button>
         </ButtonGroup>
         <ButtonGroup style="margin-left:8px;">
           <Button @click="undo"><Icon type="ios-undo"/></Button>
           <Button @click="redo"><Icon type="ios-redo"/></Button>
         </ButtonGroup>
         <ButtonGroup style="margin-left:8px;">
           <Button @click="zoomOut">-</Button>
           <span class="zoom">{{ Math.round(scale*100) }}%</span>
           <Button @click="zoomIn">+</Button>
         </ButtonGroup>
         <Button type="primary" class="save-btn" :loading="saving" :disabled="saving" @click="save"><Icon type="ios-checkmark"/> 保存</Button>
       </div>
     </div>
   </div>
</template>

<script>
import axios from 'axios'
import { getUpToken } from '@/request/api'
export default {
  name: 'smartReferenceEditor',
  props: {
    src: { type: String, required: true },
    brushSize: { type: Number, default: 24 },
    eraserSize: { type: Number, default: 24 },
    brushColor: { type: String, default: 'rgba(64,195,255,1)' },
    maskOpacity: { type: Number, default: 0.5 },
  },
  data() {
    return {
      scale: 1,
      panX: 0,
      panY: 0,
      isPanning: false,
      panStartClientX: 0,
      panStartClientY: 0,
      panStartX: 0,
      panStartY: 0,
      spacePressed: false,
      tool: 'brush',
      isDrawing: false,
      canvasW: 400,
      canvasH: 400,
      img: null,
      maskCanvas: null,
      maskCtx: null,
      ctx: null,
      startX: 0,
      startY: 0,
      currentX: null,
      currentY: null,
      history: [],
      redoStack: [],
      cursorX: null,
      cursorY: null,
      showCursor: false,
      curBrushSize: this.brushSize,
      curEraserSize: this.eraserSize,
      saving: false,
    }
  },
  computed: {
    rectColor() {
      return 'rgba(64,195,255,1)'
    },
    activeSize: {
      get() {
        return this.tool === 'brush' ? this.curBrushSize : this.curEraserSize
      },
      set(v) {
        if (this.tool === 'brush') this.curBrushSize = v
        else this.curEraserSize = v
      }
    }
  },
  mounted() {
    this.init()
  },
  watch: {
    src(n) {
      if (n) this.loadImage(n)
    }
  },
  beforeDestroy() {
    this.cleanupEvents()
  },
  methods: {
    init() {
      this.canvas = this.$refs.canvas
      this.ctx = this.canvas.getContext('2d')
      this.maskCanvas = document.createElement('canvas')
      this.maskCtx = this.maskCanvas.getContext('2d')
      this.loadImage(this.src)
      this.bindEvents()
      this.setScale(0.3)
    },
    loadImage(url) {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        this.img = img
        this.canvasW = img.width
        this.canvasH = img.height
        this.canvas.width = this.canvasW
        this.canvas.height = this.canvasH
        this.maskCanvas.width = this.canvasW
        this.maskCanvas.height = this.canvasH
        this.drawAll()
        // 初始快照
        this.pushHistory(true)
      }
      img.src = url
    },
    bindEvents() {
      this.onDown = this.onDown.bind(this)
      this.onMove = this.onMove.bind(this)
      this.onUp = this.onUp.bind(this)
      this.onEnter = this.onEnter.bind(this)
      this.onLeave = this.onLeave.bind(this)
      this.onPanDown = this.onPanDown.bind(this)
      this.onPanMove = this.onPanMove.bind(this)
      this.onPanUp = this.onPanUp.bind(this)
      this.onKeyDown = this.onKeyDown.bind(this)
      this.onKeyUp = this.onKeyUp.bind(this)
      this.canvas.addEventListener('mousedown', this.onDown)
      this.canvas.addEventListener('mousemove', this.onMove)
      this.canvas.addEventListener('mouseenter', this.onEnter)
      this.canvas.addEventListener('mouseleave', this.onLeave)
      window.addEventListener('mouseup', this.onUp)
      const viewport = this.$refs.stageViewport
      viewport && viewport.addEventListener('mousedown', this.onPanDown)
      window.addEventListener('mousemove', this.onPanMove)
      window.addEventListener('mouseup', this.onPanUp)
      window.addEventListener('keydown', this.onKeyDown)
      window.addEventListener('keyup', this.onKeyUp)
    },
    cleanupEvents() {
      this.canvas && this.canvas.removeEventListener('mousedown', this.onDown)
      this.canvas && this.canvas.removeEventListener('mousemove', this.onMove)
      this.canvas && this.canvas.removeEventListener('mouseenter', this.onEnter)
      this.canvas && this.canvas.removeEventListener('mouseleave', this.onLeave)
      window.removeEventListener('mouseup', this.onUp)
      const viewport = this.$refs.stageViewport
      viewport && viewport.removeEventListener('mousedown', this.onPanDown)
      window.removeEventListener('mousemove', this.onPanMove)
      window.removeEventListener('mouseup', this.onPanUp)
      window.removeEventListener('keydown', this.onKeyDown)
      window.removeEventListener('keyup', this.onKeyUp)
    },
    toCanvasCoord(e) {
      const rect = this.canvas.getBoundingClientRect()
      const x = (e.clientX - rect.left) * (this.canvas.width / rect.width)
      const y = (e.clientY - rect.top) * (this.canvas.height / rect.height)
      return { x, y }
    },
    onEnter() {
      this.showCursor = true
    },
    onLeave() {
      this.showCursor = false
      this.drawAll()
    },
    onDown(e) {
      if (this.isPanning) return
      const { x, y } = this.toCanvasCoord(e)
      this.isDrawing = true
      this.startX = x
      this.startY = y
      this.cursorX = x
      this.cursorY = y
      if (this.tool === 'brush') {
        // 先点绘一个起始圆点，再进入连续路径绘制
        this.maskCtx.save()
        this.maskCtx.globalCompositeOperation = 'source-over'
        this.maskCtx.fillStyle = this.brushColor
        this.maskCtx.beginPath()
        this.maskCtx.arc(x, y, this.curBrushSize / 2, 0, Math.PI * 2)
        this.maskCtx.fill()
        this.maskCtx.restore()

        this.maskCtx.globalCompositeOperation = 'source-over'
        this.maskCtx.strokeStyle = this.brushColor
        this.maskCtx.lineWidth = this.curBrushSize
        this.maskCtx.lineCap = 'round'
        this.maskCtx.lineJoin = 'round'
        this.maskCtx.beginPath()
        this.maskCtx.moveTo(x, y)
        this.drawAll()
      } else if (this.tool === 'eraser') {
        // 橡皮擦同样连续路径绘制
        this.maskCtx.save()
        this.maskCtx.globalCompositeOperation = 'destination-out'
        this.maskCtx.beginPath()
        this.maskCtx.arc(x, y, this.curEraserSize / 2, 0, Math.PI * 2)
        this.maskCtx.fill()
        this.maskCtx.restore()

        this.maskCtx.globalCompositeOperation = 'destination-out'
        this.maskCtx.lineWidth = this.curEraserSize
        this.maskCtx.lineCap = 'round'
        this.maskCtx.lineJoin = 'round'
        this.maskCtx.beginPath()
        this.maskCtx.moveTo(x, y)
        this.drawAll()
      } else if (this.tool === 'rect') {
        this.currentX = x
        this.currentY = y
        this.drawAll(true)
      }
    },
    onMove(e) {
      const { x, y } = this.toCanvasCoord(e)
      this.cursorX = x
      this.cursorY = y
      if (!this.isDrawing) {
        // 非绘制状态仅更新光标预览
        this.drawAll()
        return
      }
      if (this.tool === 'brush') {
        this.maskCtx.globalCompositeOperation = 'source-over'
        this.maskCtx.lineWidth = this.curBrushSize
        this.maskCtx.lineTo(x, y)
        this.maskCtx.stroke()
        this.startX = x
        this.startY = y
        this.drawAll()
      } else if (this.tool === 'eraser') {
        this.maskCtx.globalCompositeOperation = 'destination-out'
        this.maskCtx.lineWidth = this.curEraserSize
        this.maskCtx.lineTo(x, y)
        this.maskCtx.stroke()
        this.startX = x
        this.startY = y
        this.drawAll()
      } else if (this.tool === 'rect') {
        this.currentX = x
        this.currentY = y
        this.drawAll(true)
      }
    },
    onUp(e) {
      if (!this.isDrawing) return
      this.isDrawing = false
      if (this.tool === 'rect') {
        const sx = Math.min(this.startX, this.currentX || this.startX)
        const sy = Math.min(this.startY, this.currentY || this.startY)
        const w = Math.abs((this.currentX || this.startX) - this.startX)
        const h = Math.abs((this.currentY || this.startY) - this.startY)
        if (w > 0 && h > 0) {
          this.maskCtx.globalCompositeOperation = 'source-over'
          // 在蒙版上用不透明色绘制矩形，最终透明度由 maskOpacity 控制
          this.maskCtx.fillStyle = this.rectColor
          this.maskCtx.fillRect(sx, sy, w, h)
          this.pushHistory()
        }
        this.currentX = null
        this.currentY = null
        this.drawAll()
      } else {
        // 结束连续路径
        try { this.maskCtx.closePath() } catch(e) {}
        this.pushHistory()
        this.drawAll()
      }
    },
    onPanDown(e) {
      // 按住空格或使用中键/右键拖拽以平移视图
      if (!(this.spacePressed || e.button === 1 || e.button === 2)) return
      e.preventDefault()
      this.isPanning = true
      const viewport = this.$refs.stageViewport
      viewport && viewport.classList.add('grabbing')
      this.panStartClientX = e.clientX
      this.panStartClientY = e.clientY
      this.panStartX = this.panX
      this.panStartY = this.panY
    },
    onPanMove(e) {
      if (!this.isPanning) return
      const dx = e.clientX - this.panStartClientX
      const dy = e.clientY - this.panStartClientY
      this.panX = this.panStartX + dx
      this.panY = this.panStartY + dy
      this.updateTransform()
    },
    onPanUp() {
      if (!this.isPanning) return
      this.isPanning = false
      const viewport = this.$refs.stageViewport
      viewport && viewport.classList.remove('grabbing')
    },
    onKeyDown(e) { if (e.code === 'Space') this.spacePressed = true },
    onKeyUp(e) { if (e.code === 'Space') this.spacePressed = false },
    drawAll(previewRect = false) {
      const ctx = this.ctx
      ctx.save()
      ctx.clearRect(0, 0, this.canvasW, this.canvasH)
      // 背景图
      if (this.img) ctx.drawImage(this.img, 0, 0, this.canvasW, this.canvasH)
      // 半透明蒙版（统一由 maskOpacity 控制），避免笔迹重叠导致不透明
      ctx.globalAlpha = this.maskOpacity
      ctx.drawImage(this.maskCanvas, 0, 0)
      ctx.globalAlpha = 1.0
      // 矩形预览
      if (previewRect && this.tool === 'rect') {
        const sx = Math.min(this.startX, this.currentX || this.startX)
        const sy = Math.min(this.startY, this.currentY || this.startY)
        const w = Math.abs((this.currentX || this.startX) - this.startX)
        const h = Math.abs((this.currentY || this.startY) - this.startY)
        ctx.fillStyle = this.rectColor
        ctx.globalAlpha = 0.4
        ctx.fillRect(sx, sy, w, h)
        ctx.globalAlpha = 1.0
        ctx.strokeStyle = '#00d3ff'
        ctx.lineWidth = 2
        ctx.strokeRect(sx, sy, w, h)
      }
      // 画笔大小与位置预览
      this.drawBrushCursor(ctx)
      ctx.restore()
    },
    drawBrushCursor(ctx) {
      if (!this.showCursor) return
      if (this.tool !== 'brush' && this.tool !== 'eraser') return
      if (this.cursorX == null || this.cursorY == null) return
      const r = (this.tool === 'brush' ? this.curBrushSize : this.curEraserSize) / 2
      ctx.save()
      ctx.globalAlpha = 1.0
      ctx.strokeStyle = '#00d3ff'
      ctx.setLineDash([6, 4])
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(this.cursorX, this.cursorY, r, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    },
    pushHistory(initial = false) {
      try {
        const snap = this.maskCtx.getImageData(0, 0, this.maskCanvas.width, this.maskCanvas.height)
        this.history.push(snap)
        if (!initial) this.redoStack = []
      } catch (e) {}
    },
    undo() {
      if (!this.history.length) return
      const last = this.history.pop()
      if (last) this.redoStack.push(last)
      const prev = this.history[this.history.length - 1]
      if (prev) {
        this.maskCtx.putImageData(prev, 0, 0)
      } else {
        this.maskCtx.clearRect(0, 0, this.maskCanvas.width, this.maskCanvas.height)
      }
      this.drawAll()
    },
    redo() {
      const snap = this.redoStack.pop()
      if (!snap) return
      this.history.push(snap)
      this.maskCtx.putImageData(snap, 0, 0)
      this.drawAll()
    },
    setTool(t) {
      this.tool = t
    },
    zoomIn() {
      this.setScale(this.scale + 0.1)
    },
    zoomOut() {
      this.setScale(Math.max(0.1, this.scale - 0.1))
    },
    setScale(v) {
      this.scale = v
      this.updateTransform()
    },
    updateTransform() {
      const wrap = this.$refs.stageWrap
      if (wrap) {
        wrap.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`
        wrap.style.transformOrigin = 'top left'
      }
    },
    async save() {
      if (this.saving) return
      this.saving = true
      const out = document.createElement('canvas')
      out.width = this.canvasW
      out.height = this.canvasH
      const octx = out.getContext('2d')
      // 背景底图
      if (this.img) octx.drawImage(this.img, 0, 0, this.canvasW, this.canvasH)
      // 用预览一致的透明度叠加蒙版
      octx.globalAlpha = this.maskOpacity
      octx.drawImage(this.maskCanvas, 0, 0)
      octx.globalAlpha = 1.0
      // 混合后的最终图
      const imageDataUrl = out.toDataURL('image/png')
      // 同步保留原始蒙版（如有需要）
      const maskDataUrl = this.maskCanvas.toDataURL('image/png')

      const toBlob = (dataUrl) => {
        const arr = dataUrl.split(',')
        const mime = arr[0].match(/:(.*?);/)[1]
        const bstr = atob(arr[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)
        while (n--) u8arr[n] = bstr.charCodeAt(n)
        return new Blob([u8arr], { type: mime })
      }

      try {
        const { data } = await getUpToken()
        const uptoken = data && data.data && data.data.uptoken

        const upload = async (blob, filename) => {
          const formData = new FormData()
          formData.append('token', uptoken)
          formData.append('file', blob, filename || 'smart_ref_mix.png')
          const res = await axios.post('https://up-z2.qiniup.com', formData)
          return 'https://qy-oss.yolanda.hk/' + res.data.key
        }

        const imageUrl = await upload(toBlob(imageDataUrl), 'smart_ref_mix.png')

        this.$emit('save', { imageDataUrl, maskDataUrl, imageUrl })
        this.$Message && this.$Message.success('已保存')
      } catch (e) {
        console.error('save error', e)
        this.$Message && this.$Message.error('保存失败')
        this.$emit('save', { imageDataUrl, maskDataUrl })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.editor-root { background: #1f1f1f; color: #cfd3dc; padding: 12px; border-radius: 6px; }
.header { margin-bottom: 8px; font-size: 14px; }
.stage-viewport { height: 400px; overflow: auto; background: #111; margin: 0 auto; border: 1px solid #333; position: relative; cursor: grab; }
.stage-viewport.grabbing { cursor: grabbing; }
.stage { width: fit-content; }
 .toolbar { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; margin-top: 12px; }
 .tool-buttons { display:flex; align-items:center; gap:8px; width:100%; }
 .save-btn { margin-left:auto; }
 .size-tool { display:flex; align-items:center; gap:8px; width:100%; }
 .size-text { color:#cfd3dc; }
 .zoom { display: inline-block; width: 56px; text-align: center; color: #cfd3dc; }
 canvas { display: block; cursor: crosshair; }
</style>


// WEBPACK FOOTER //
// src/components/common/smart_reference_editor.vue